(() => {
  const STORAGE_KEY_PREFIX = 'meal-modal-state:';
  const PURCHASE_OPTION_SESSION_STORAGE_KEY = 'mealtime-cart-purchase-option';
  const PURCHASE_OPTION_MODES = ['one_time', 'weekly', 'biweekly', 'monthly'];
  const MODAL_OPEN_CLASS = 'is-open';
  const MODAL_CLOSING_CLASS = 'is-closing';
  const MODAL_PANEL_SELECTOR = '.collection-meal-modal__panel';
  const MODAL_HISTORY_SOURCE = 'collection-meal-modal';
  const MODAL_HISTORY_STATE_KEY = '__collectionMealModal';
  const CARD_INITIALIZED_ATTRIBUTE = 'data-collection-meal-card-initialized';
  const CARD_OPEN_MAX_PRESS_MS = 350;
  const CARD_OPEN_INTERACTIVE_SELECTOR = [
    'a',
    'button',
    'input',
    'select',
    'textarea',
    'label',
    'summary',
    '[role="button"]',
    '[role="link"]',
    '[role="checkbox"]',
    '[role="switch"]',
    '[role="menuitem"]',
    '[contenteditable=""]',
    '[contenteditable="true"]',
  ].join(', ');
  const CLOSE_FALLBACK_BUFFER_MS = 50;
  let activeModal = null;
  let bodyScrollLocked = false;
  let lockedScrollY = 0;
  let historyNavigationPending = false;
  let initCardsFrame = 0;
  const modalCloseTimers = new WeakMap();
  const modalOpenFrames = new WeakMap();
  const modalCloseHandlers = new WeakMap();
  const modalCloseCallbacks = new WeakMap();
  const modalKeys = new WeakMap();
  const modalRegistry = new Map();

  function parseTransitionTimeToMs(value) {
    const time = String(value || '').trim();
    if (!time) return 0;
    if (time.endsWith('ms')) return Number.parseFloat(time) || 0;
    if (time.endsWith('s')) return (Number.parseFloat(time) || 0) * 1000;
    return 0;
  }

  function getLongestTransitionMs(element) {
    if (!element || typeof window.getComputedStyle !== 'function') return 0;
    const styles = window.getComputedStyle(element);
    const durationValues = styles.transitionDuration.split(',');
    const delayValues = styles.transitionDelay.split(',');
    const count = Math.max(durationValues.length, delayValues.length);
    let maxTime = 0;

    for (let index = 0; index < count; index += 1) {
      const duration = parseTransitionTimeToMs(durationValues[index] || durationValues[durationValues.length - 1]);
      const delay = parseTransitionTimeToMs(delayValues[index] || delayValues[delayValues.length - 1]);
      maxTime = Math.max(maxTime, duration + delay);
    }

    return maxTime;
  }

  function shouldReduceMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clearModalCloseTimer(modal) {
    const closeTimer = modalCloseTimers.get(modal);
    if (typeof closeTimer === 'number') {
      window.clearTimeout(closeTimer);
      modalCloseTimers.delete(modal);
    }
  }

  function clearModalOpenFrame(modal) {
    const openFrame = modalOpenFrames.get(modal);
    if (typeof openFrame === 'number') {
      window.cancelAnimationFrame(openFrame);
      modalOpenFrames.delete(modal);
    }
  }

  function detachModalCloseHandler(modal) {
    const panel = modal.querySelector(MODAL_PANEL_SELECTOR);
    const handler = modalCloseHandlers.get(modal);
    if (panel && handler) {
      panel.removeEventListener('transitionend', handler);
    }
    modalCloseHandlers.delete(modal);
  }

  function clearModalTransitionState(modal) {
    clearModalCloseTimer(modal);
    clearModalOpenFrame(modal);
    detachModalCloseHandler(modal);
  }

  function runModalCloseCallbacks(modal) {
    const callbacks = modalCloseCallbacks.get(modal);
    if (!Array.isArray(callbacks) || !callbacks.length) return;

    modalCloseCallbacks.delete(modal);
    callbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('Meal modal close callback failed', error);
      }
    });
  }

  function finalizeModalClose(modal, options = {}) {
    const { keepBodyScrollLocked = false } = options;
    clearModalTransitionState(modal);
    modal.classList.remove(MODAL_OPEN_CLASS, MODAL_CLOSING_CLASS);
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');

    if (activeModal === modal) {
      activeModal = null;
      if (!keepBodyScrollLocked) {
        unlockBodyScroll();
      }
    }

    runModalCloseCallbacks(modal);
  }

  function enforceWindowScrollLock() {
    if (!bodyScrollLocked) return;
    const currentScrollY = window.scrollY || window.pageYOffset || 0;
    if (currentScrollY !== lockedScrollY) {
      window.scrollTo(0, lockedScrollY);
    }
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function normalizeModalKeyPart(value) {
    const normalizedValue = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return normalizedValue || 'unknown';
  }

  function getEventTargetElement(event) {
    const { target } = event;
    if (target instanceof Element) return target;
    if (target && target.parentElement instanceof Element) return target.parentElement;
    return null;
  }

  function shouldIgnoreCardOpenClick(event) {
    const targetElement = getEventTargetElement(event);
    if (!targetElement) return true;
    if (targetElement.closest('[data-collection-item-modal]')) return true;
    return !!targetElement.closest(CARD_OPEN_INTERACTIVE_SELECTOR);
  }

  function getMode(config) {
    return normalize(config && config.mode) === 'edit' ? 'edit' : 'add';
  }

  function createModalKey(config, cardIndex) {
    const mode = getMode(config);
    const semanticId =
      mode === 'edit'
        ? normalizeModalKeyPart(config.editBundleId || config.productHandle || config.productId || '')
        : normalizeModalKeyPart(config.productHandle || config.productId || '');

    return `${mode}:${semanticId}:${cardIndex}`;
  }

  function registerModalController(modalKey, controller) {
    if (!modalKey || !controller || !controller.modal) return;
    modalRegistry.set(modalKey, controller);
    modalKeys.set(controller.modal, modalKey);
  }

  function getModalKey(modal) {
    if (!modal) return '';
    return modalKeys.get(modal) || '';
  }

  function queueModalCloseCallback(modal, callback) {
    if (!modal || typeof callback !== 'function') return;
    const queuedCallbacks = modalCloseCallbacks.get(modal) || [];
    queuedCallbacks.push(callback);
    modalCloseCallbacks.set(modal, queuedCallbacks);
  }

  function supportsModalHistory() {
    return !!(
      window.history &&
      typeof window.history.pushState === 'function' &&
      typeof window.history.replaceState === 'function' &&
      typeof window.history.back === 'function'
    );
  }

  function getModalHistoryState(state = window.history.state) {
    if (!state || typeof state !== 'object' || Array.isArray(state)) return null;

    const modalState = state[MODAL_HISTORY_STATE_KEY];
    if (!modalState || typeof modalState !== 'object') return null;
    if (modalState.source !== MODAL_HISTORY_SOURCE) return null;

    const modalKey = String(modalState.modalKey || '').trim();
    if (!modalKey) return null;

    return { modalKey };
  }

  function buildModalHistoryState(modalKey) {
    const currentState =
      window.history.state && typeof window.history.state === 'object' && !Array.isArray(window.history.state)
        ? { ...window.history.state }
        : {};

    currentState[MODAL_HISTORY_STATE_KEY] = {
      source: MODAL_HISTORY_SOURCE,
      modalKey,
    };

    return currentState;
  }

  function syncModalHistoryState(modalKey) {
    if (!supportsModalHistory() || !modalKey) return false;

    const currentModalState = getModalHistoryState();
    if (currentModalState && currentModalState.modalKey === modalKey) return true;

    const method = currentModalState ? 'replaceState' : 'pushState';
    window.history[method](buildModalHistoryState(modalKey), '', window.location.href);
    return true;
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  function normalizePurchaseOptionMode(value) {
    const normalizedValue = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/-/g, '_');

    return PURCHASE_OPTION_MODES.includes(normalizedValue) ? normalizedValue : '';
  }

  function normalizePlanId(value) {
    if (value === null || typeof value === 'undefined' || value === '') return null;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
  }

  function normalizePurchaseOptions(rawOptions, legacySellingPlanId) {
    const purchaseOptions = {
      one_time: null,
      weekly: null,
      biweekly: null,
      monthly: null,
    };

    if (rawOptions && typeof rawOptions === 'object') {
      PURCHASE_OPTION_MODES.forEach((mode) => {
        purchaseOptions[mode] = mode === 'one_time' ? null : normalizePlanId(rawOptions[mode]);
      });
    }

    if (!purchaseOptions.weekly) {
      purchaseOptions.weekly = normalizePlanId(legacySellingPlanId);
    }

    return purchaseOptions;
  }

  function getVariantPurchaseOptions(variant) {
    if (!variant || typeof variant !== 'object') {
      return normalizePurchaseOptions(null, null);
    }

    return normalizePurchaseOptions(variant.purchaseOptions, variant.sellingPlanId);
  }

  function getSaucePurchaseOptions(input) {
    if (!input) {
      return normalizePurchaseOptions(null, null);
    }

    let parsedOptions = null;
    const serializedOptions = input.dataset.saucePurchaseOptions;
    if (serializedOptions) {
      try {
        parsedOptions = JSON.parse(serializedOptions);
      } catch (error) {
        parsedOptions = null;
      }
    }

    return normalizePurchaseOptions(parsedOptions, input.dataset.sauceSellingPlan);
  }

  function getStoredPurchaseOptionMode() {
    try {
      return normalizePurchaseOptionMode(
        window.sessionStorage.getItem(PURCHASE_OPTION_SESSION_STORAGE_KEY)
      );
    } catch (error) {
      return '';
    }
  }

  function setStoredPurchaseOptionMode(mode) {
    const normalizedMode = normalizePurchaseOptionMode(mode);
    if (!normalizedMode) return;

    try {
      window.sessionStorage.setItem(PURCHASE_OPTION_SESSION_STORAGE_KEY, normalizedMode);
    } catch (error) {
      // Ignore storage failures.
    }
  }

  function inferPurchaseOptionModeFromPlan(planSource) {
    const plan = planSource?.selling_plan || planSource;
    if (!plan) return '';

    const optionsText = Array.isArray(plan.options) ? plan.options.join(' ') : '';
    const searchText = `${plan.name || ''} ${plan.description || ''} ${optionsText}`.toLowerCase();

    if (/bi[\s-]?weekly|every\s*2\s*weeks?|2\s*weeks?|fortnight/.test(searchText)) {
      return 'biweekly';
    }

    if (/weekly|every\s*week|1\s*week/.test(searchText)) {
      return 'weekly';
    }

    return '';
  }

  function getLineItemPurchaseOptionMode(lineItem) {
    if (!lineItem || typeof lineItem !== 'object') return 'one_time';

    const inferredMode = inferPurchaseOptionModeFromPlan(lineItem.selling_plan_allocation);
    if (inferredMode) return inferredMode;

    return lineItem.selling_plan_allocation ? 'weekly' : 'one_time';
  }

  async function resolvePurchaseOptionMode(config, overrideMode) {
    const normalizedOverride = normalizePurchaseOptionMode(overrideMode);
    if (normalizedOverride) return normalizedOverride;

    const storedMode = getStoredPurchaseOptionMode();
    if (storedMode) return storedMode;

    const configMode = normalizePurchaseOptionMode(config && config.currentPurchaseOption);
    if (configMode) return configMode;

    return 'weekly';
  }

  function resolveSellingPlanId(purchaseOptions, purchaseOptionMode) {
    const normalizedMode = normalizePurchaseOptionMode(purchaseOptionMode);
    if (!normalizedMode || normalizedMode === 'one_time') return null;

    const normalizedOptions = normalizePurchaseOptions(purchaseOptions, null);
    return normalizePlanId(normalizedOptions[normalizedMode]);
  }

  function canApplyPurchaseOptionToBundle(variant, selectedSauceInputs, purchaseOptionMode) {
    const normalizedMode = normalizePurchaseOptionMode(purchaseOptionMode);
    if (!normalizedMode || normalizedMode === 'one_time') return true;

    if (!resolveSellingPlanId(getVariantPurchaseOptions(variant), normalizedMode)) {
      return false;
    }

    return selectedSauceInputs.every((input) => resolveSellingPlanId(getSaucePurchaseOptions(input), normalizedMode));
  }

  function createBundleId() {
    return Math.random().toString(36).slice(2, 12);
  }

  function parseConfig(card) {
    const configNode = card.querySelector('[data-collection-meal-config]');
    if (!configNode) return null;

    try {
      return JSON.parse(configNode.textContent || '{}');
    } catch (error) {
      console.warn('Failed to parse meal modal config', error);
      return null;
    }
  }

  function getStoredState(handle) {
    if (!handle) return null;
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY_PREFIX + handle);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function setStoredState(handle, state) {
    if (!handle) return;
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY_PREFIX + handle,
        JSON.stringify({
          size: state.size,
          protein: state.protein,
          sauces: Array.from(state.sauces || []),
          quantity: state.quantity,
        })
      );
    } catch (error) {
      // Ignore storage failures.
    }
  }

  function resolveVariant(config, state) {
    const variants = Array.isArray(config.variants) ? config.variants : [];

    return (
      variants.find((variant) => {
        const sizeMatches = !config.hasSizeOption || normalize(variant.size) === normalize(state.size);
        const proteinMatches =
          !config.hasProteinOption || normalize(variant.protein) === normalize(state.protein);
        return sizeMatches && proteinMatches;
      }) || null
    );
  }

  function resolveVariantForOptions(config, sizeValue, proteinValue) {
    const variants = Array.isArray(config.variants) ? config.variants : [];
    const normalizedSize = normalize(sizeValue);
    const normalizedProtein = normalize(proteinValue);

    return (
      variants.find((variant) => {
        const sizeMatches = !config.hasSizeOption || normalize(variant.size) === normalizedSize;
        const proteinMatches = !config.hasProteinOption || normalize(variant.protein) === normalizedProtein;
        return sizeMatches && proteinMatches;
      }) || null
    );
  }

  function getSauceInputs(modal) {
    return Array.from(modal.querySelectorAll('[data-sauce-option]'));
  }

  function readSaucePrice(input) {
    const cents = Number(input.dataset.saucePrice);
    return Number.isFinite(cents) ? cents : 0;
  }

  function readSauceNutritionValue(input, field) {
    const value = Number(input.dataset[field]);
    return Number.isFinite(value) ? value : 0;
  }

  function renderOptionButtons(modal, group, selectedValue) {
    modal.querySelectorAll(`[data-option-button][data-option-group-name="${group}"]`).forEach((button) => {
      const isActive = normalize(button.dataset.optionValue) === normalize(selectedValue);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function getSauceTotal(modal, state) {
    let total = 0;
    getSauceInputs(modal).forEach((input) => {
      if (state.sauces.has(input.dataset.sauceId)) {
        total += readSaucePrice(input);
      }
    });
    return total;
  }

  function getSauceNutritionTotals(modal, state) {
    const totals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      sodium: 0,
    };

    getSauceInputs(modal).forEach((input) => {
      if (!state.sauces.has(input.dataset.sauceId)) return;

      totals.calories += readSauceNutritionValue(input, 'sauceCalories');
      totals.protein += readSauceNutritionValue(input, 'sauceProtein');
      totals.carbs += readSauceNutritionValue(input, 'sauceCarbs');
      totals.fat += readSauceNutritionValue(input, 'sauceFat');
      totals.sodium += readSauceNutritionValue(input, 'sauceSodium');
    });

    return totals;
  }

  function setNutrition(modal, variant, sauceNutrition) {
    const hasVariant = !!variant;
    const values = {
      calories: hasVariant ? Number(variant.calories || 0) + Number(sauceNutrition.calories || 0) : 0,
      protein: hasVariant ? Number(variant.proteinGrams || 0) + Number(sauceNutrition.protein || 0) : 0,
      carbs: hasVariant ? Number(variant.carbs || 0) + Number(sauceNutrition.carbs || 0) : 0,
      fat: hasVariant ? Number(variant.fat || 0) + Number(sauceNutrition.fat || 0) : 0,
      sodium: hasVariant ? Number(variant.sodium || 0) + Number(sauceNutrition.sodium || 0) : 0,
    };

    Object.keys(values).forEach((field) => {
      const target = modal.querySelector(`[data-modal-nutrition="${field}"]`);
      if (target) target.textContent = String(values[field] || 0);
    });
  }

  function updateSizeOptionPrices(modal, config, state) {
    modal.querySelectorAll('[data-option-button][data-option-group-name="size"]').forEach((button) => {
      const sizeValue = normalize(button.dataset.optionValue);
      const variantForCurrentProtein = resolveVariantForOptions(config, sizeValue, state.protein);
      const fallbackVariant = resolveVariantForOptions(config, sizeValue, config.defaultProtein || '');
      const variant = variantForCurrentProtein || fallbackVariant;
      const priceTarget = button.querySelector('[data-size-option-price]');

      if (variant && priceTarget) {
        priceTarget.textContent = formatMoney(Number(variant.price || 0));
      }
    });
  }

  function updateProteinOptionPrices(modal, config, state) {
    const selectedSize = state.size || config.defaultSize || '';
    const baseVariant =
      resolveVariantForOptions(config, selectedSize, config.defaultProtein || '') ||
      resolveVariantForOptions(config, selectedSize, state.protein);
    const basePrice = baseVariant ? Number(baseVariant.price || 0) : 0;

    modal.querySelectorAll('[data-option-button][data-option-group-name="protein"]').forEach((button) => {
      const proteinValue = normalize(button.dataset.optionValue);
      const variant = resolveVariantForOptions(config, selectedSize, proteinValue);
      const delta = variant ? Number(variant.price || 0) - basePrice : 0;
      const priceTarget = button.querySelector('.collection-meal-modal__option-price');
      if (!priceTarget) return;

      if (!variant || delta === 0) {
        priceTarget.textContent = '';
        priceTarget.hidden = true;
        return;
      }

      priceTarget.hidden = false;
      priceTarget.textContent = `${delta > 0 ? '+' : ''}${formatMoney(delta)}`;
    });
  }

  function setAvailability(modal, variant) {
    const status = modal.querySelector('[data-modal-availability]');
    if (!status) return;

    if (!variant) {
      status.textContent = 'This option combination is unavailable.';
      return;
    }

    if (!variant.available) {
      status.textContent = 'Selected meal option is sold out right now.';
      return;
    }

    status.textContent = '';
  }

  function setSubmitState(modal, canSubmit, submitting, config) {
    const submit = modal.querySelector('[data-modal-submit]');
    if (!submit) return;

    submit.disabled = !canSubmit || submitting;
    if (submitting) {
      submit.textContent = getMode(config) === 'edit' ? 'Updating...' : 'Adding...';
    }
  }

  function updateSubmitLabel(modal, totalPrice, submitting, config) {
    const submit = modal.querySelector('[data-modal-submit]');
    if (!submit) return;

    const formattedTotal = formatMoney(totalPrice);
    const submitPrefix = String(config && config.submitLabel ? config.submitLabel : 'Add to cart');

    if (submitting) {
      const total = modal.querySelector('[data-modal-total]');
      if (total) total.textContent = formattedTotal;
      return;
    }

    submit.innerHTML = `${submitPrefix} · <span data-modal-total>${formattedTotal}</span>`;
  }

  function render(card, config, state, submitting) {
    const modal = card.querySelector('[data-collection-item-modal]');
    if (!modal) return;

    const variant = resolveVariant(config, state);

    renderOptionButtons(modal, 'size', state.size);
    renderOptionButtons(modal, 'protein', state.protein);
    updateSizeOptionPrices(modal, config, state);
    updateProteinOptionPrices(modal, config, state);

    getSauceInputs(modal).forEach((input) => {
      input.checked = state.sauces.has(input.dataset.sauceId);
    });

    const qtyInput = modal.querySelector('[data-modal-quantity-input]');
    if (qtyInput) qtyInput.value = String(state.quantity);

    const sauceNutrition = getSauceNutritionTotals(modal, state);
    setNutrition(modal, variant, sauceNutrition);
    setAvailability(modal, variant);

    const basePrice = variant ? Number(variant.price || 0) : 0;
    const sauceTotal = getSauceTotal(modal, state);
    const totalPrice = (basePrice + sauceTotal) * state.quantity;

    updateSubmitLabel(modal, totalPrice, submitting, config);

    const canSubmit = !!variant && !!variant.available;
    setSubmitState(modal, canSubmit, submitting, config);
  }

  function showFeedback(card, message) {
    const feedback = card.querySelector('[data-card-feedback]');
    if (!feedback) return;

    feedback.textContent = message;
    feedback.hidden = false;

    window.setTimeout(() => {
      feedback.hidden = true;
    }, 3000);
  }

  async function addToCart(config, state, modal, options = {}) {
    const variant = resolveVariant(config, state);
    if (!variant || !variant.available) {
      return { ok: false, error: 'Selected meal option is unavailable.' };
    }

    const bundleId = createBundleId();
    const selectedSauceInputs = getSauceInputs(modal).filter(
      (input) => state.sauces.has(input.dataset.sauceId) && !input.disabled
    );
    const extraSauceValue = selectedSauceInputs
      .map((input) => String(input.dataset.sauceName || '').trim())
      .filter(Boolean)
      .join(', ');
    const requestedPurchaseOption = await resolvePurchaseOptionMode(config, options.purchaseOptionMode);
    const appliedPurchaseOption = canApplyPurchaseOptionToBundle(
      variant,
      selectedSauceInputs,
      requestedPurchaseOption
    )
      ? requestedPurchaseOption
      : 'one_time';
    setStoredPurchaseOptionMode(appliedPurchaseOption);

    const items = [
      {
        id: variant.id,
        quantity: state.quantity,
        properties: {
          _bundle_id: bundleId,
          ...(extraSauceValue ? { _extra_sauce: extraSauceValue } : {}),
        },
      },
    ];

    const parentSellingPlanId = resolveSellingPlanId(
      getVariantPurchaseOptions(variant),
      appliedPurchaseOption
    );
    if (parentSellingPlanId) {
      items[0].selling_plan = parentSellingPlanId;
    }

    selectedSauceInputs.forEach((input) => {
      const sauceId = Number(input.dataset.sauceId);
      if (!Number.isFinite(sauceId)) return;

      const sauceItem = {
        id: sauceId,
        quantity: state.quantity,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: 'child',
        },
      };

      const saucePlan = resolveSellingPlanId(getSaucePurchaseOptions(input), appliedPurchaseOption);
      if (saucePlan) {
        sauceItem.selling_plan = saucePlan;
      }

      items.push(sauceItem);
    });

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      let errorMessage = 'Unable to add item to cart.';
      try {
        const errorBody = await response.json();
        if (errorBody && errorBody.description) {
          errorMessage = errorBody.description;
        }
      } catch (error) {
        // Ignore parsing failure.
      }
      return { ok: false, error: errorMessage };
    }

    const sourceCollectionHandle = String(config.collectionHandle || '').trim().toLowerCase();
    if (sourceCollectionHandle && !options.skipCollectionEvent) {
      document.dispatchEvent(
        new CustomEvent('collection:subscription-add', {
          detail: {
            collectionHandle: sourceCollectionHandle,
            addedQuantity: state.quantity,
          },
        })
      );
    }

    if (!options.skipFetchCart && typeof window.fetchCart === 'function') {
      window.fetchCart();
    }

    return { ok: true };
  }

  async function replaceBundleInCart(config, state, modal) {
    const targetBundleId = String(config.editBundleId || '').trim();
    if (!targetBundleId) {
      return { ok: false, error: 'Unable to find the original meal bundle in cart.' };
    }

    const cartResponse = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
    });

    if (!cartResponse.ok) {
      return { ok: false, error: 'Unable to load cart data.' };
    }

    const cart = await cartResponse.json();
    const updates = {};
    let bundlePurchaseOptionMode = normalizePurchaseOptionMode(config.currentPurchaseOption);
    (cart.items || []).forEach((lineItem) => {
      if (lineItem && lineItem.properties && String(lineItem.properties._bundle_id || '') === targetBundleId) {
        if (!bundlePurchaseOptionMode && lineItem.properties._bundle_role !== 'child') {
          bundlePurchaseOptionMode = getLineItemPurchaseOptionMode(lineItem);
        }
        updates[lineItem.key] = 0;
      }
    });

    if (!Object.keys(updates).length) {
      return { ok: false, error: 'Original meal bundle was not found in cart.' };
    }

    const removeResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
      body: JSON.stringify({ updates }),
    });

    if (!removeResponse.ok) {
      return { ok: false, error: 'Unable to update cart.' };
    }

    return addToCart(config, state, modal, {
      skipCollectionEvent: true,
      skipFetchCart: true,
      purchaseOptionMode: bundlePurchaseOptionMode,
    });
  }

  function lockBodyScroll() {
    if (bodyScrollLocked) return;

    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('collection-meal-modal-open');
    document.body.classList.add('collection-meal-modal-open');
    window.addEventListener('scroll', enforceWindowScrollLock, { passive: true });
    bodyScrollLocked = true;
  }

  function unlockBodyScroll() {
    if (!bodyScrollLocked) return;

    window.removeEventListener('scroll', enforceWindowScrollLock);
    document.documentElement.classList.remove('collection-meal-modal-open');
    document.body.classList.remove('collection-meal-modal-open');
    bodyScrollLocked = false;
  }

  function closeModal(modal, options = {}) {
    const { keepBodyScrollLocked = false } = options;
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    clearModalOpenFrame(modal);

    if (shouldReduceMotion()) {
      finalizeModalClose(modal, { keepBodyScrollLocked });
      return;
    }

    const panel = modal.querySelector(MODAL_PANEL_SELECTOR);
    if (!panel) {
      finalizeModalClose(modal, { keepBodyScrollLocked });
      return;
    }

    modal.classList.remove(MODAL_OPEN_CLASS);
    modal.classList.add(MODAL_CLOSING_CLASS);

    clearModalCloseTimer(modal);
    detachModalCloseHandler(modal);

    const closeHandler = (event) => {
      if (event.target !== panel || event.propertyName !== 'transform') return;
      finalizeModalClose(modal, { keepBodyScrollLocked });
    };

    panel.addEventListener('transitionend', closeHandler);
    modalCloseHandlers.set(modal, closeHandler);

    const fallbackDuration = getLongestTransitionMs(panel) + CLOSE_FALLBACK_BUFFER_MS;
    const closeTimer = window.setTimeout(
      () => finalizeModalClose(modal, { keepBodyScrollLocked }),
      Math.max(0, fallbackDuration)
    );
    modalCloseTimers.set(modal, closeTimer);
  }

  function requestModalClose(modal, options = {}) {
    const { keepBodyScrollLocked = false, afterClose } = options;
    if (!modal) return;

    queueModalCloseCallback(modal, afterClose);

    const modalKey = getModalKey(modal);
    const currentModalState = getModalHistoryState();

    if (
      supportsModalHistory() &&
      !historyNavigationPending &&
      modalKey &&
      currentModalState &&
      currentModalState.modalKey === modalKey
    ) {
      historyNavigationPending = true;
      window.history.back();
      return;
    }

    closeModal(modal, { keepBodyScrollLocked });
  }

  function restoreModalFromHistory(modalKey) {
    if (!modalKey) return;

    const controller = modalRegistry.get(modalKey);
    if (!controller || !controller.modal) return;

    controller.rerender();
    if (activeModal !== controller.modal || controller.modal.hidden) {
      openModal(controller.modal, { fromHistory: true });
    }
  }

  function openModal(modal, options = {}) {
    const { fromHistory = false } = options;
    if (activeModal && activeModal !== modal) {
      finalizeModalClose(activeModal, { keepBodyScrollLocked: true });
    }

    const modalKey = getModalKey(modal);
    if (!fromHistory && modalKey) {
      syncModalHistoryState(modalKey);
    }

    clearModalTransitionState(modal);
    modal.classList.remove(MODAL_CLOSING_CLASS, MODAL_OPEN_CLASS);
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    activeModal = modal;
    lockBodyScroll();

    if (shouldReduceMotion()) {
      modal.classList.add(MODAL_OPEN_CLASS);
      return;
    }

    const openFrame = window.requestAnimationFrame(() => {
      modal.classList.add(MODAL_OPEN_CLASS);
      modalOpenFrames.delete(modal);
    });
    modalOpenFrames.set(modal, openFrame);
  }

  function initDescriptionToggle(wrapper, button) {
    if (!wrapper || !button) return;

    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const isExpanded = wrapper.classList.toggle('is-expanded');
      button.textContent = isExpanded ? 'Read less' : 'Read more';
    });
  }

  function initCard(card, cardIndex) {
    if (!card || card.hasAttribute(CARD_INITIALIZED_ATTRIBUTE)) return;
    const config = parseConfig(card);
    const modal = card.querySelector('[data-collection-item-modal]');
    if (!config || !modal) return;
    const mode = getMode(config);
    const modalKey = createModalKey(config, cardIndex);

    const saved = mode === 'add' ? getStoredState(config.productHandle) : null;
    const selectedSauceIds = Array.isArray(config.selectedSauceIds) ? config.selectedSauceIds.map(String) : [];

    const state = {
      size: normalize(config.selectedSize || saved?.size || config.defaultSize || ''),
      protein: normalize(config.selectedProtein || saved?.protein || config.defaultProtein || ''),
      sauces: new Set(selectedSauceIds.length ? selectedSauceIds : Array.isArray(saved?.sauces) ? saved.sauces : []),
      quantity: Math.max(1, Number(config.selectedQuantity || saved?.quantity) || 1),
    };

    let submitting = false;

    function rerender() {
      render(card, config, state, submitting);
      if (mode !== 'edit') {
        setStoredState(config.productHandle, state);
      }
    }

    registerModalController(modalKey, { modal, rerender });
    card.setAttribute(CARD_INITIALIZED_ATTRIBUTE, 'true');

    // Card description toggle
    const cardDescWrap = card.querySelector('[data-description-wrap]');
    const cardDescToggle = card.querySelector('[data-toggle-description]');
    initDescriptionToggle(cardDescWrap, cardDescToggle);

    // Modal description toggle
    const modalDescWrap = modal.querySelector('[data-modal-description-wrap]');
    const modalDescToggle = modal.querySelector('[data-toggle-modal-description]');
    initDescriptionToggle(modalDescWrap, modalDescToggle);

    if (card.hasAttribute('data-card-click-opens-modal')) {
      let cardPressStartedAt = 0;

      card.addEventListener('pointerdown', (event) => {
        if (shouldIgnoreCardOpenClick(event)) return;
        if (event.button !== 0) return;
        cardPressStartedAt = Date.now();
      });

      card.addEventListener('pointercancel', () => {
        cardPressStartedAt = 0;
      });

      card.addEventListener('click', (event) => {
        if (shouldIgnoreCardOpenClick(event)) return;
        if (cardPressStartedAt) {
          const pressDuration = Date.now() - cardPressStartedAt;
          cardPressStartedAt = 0;
          if (pressDuration > CARD_OPEN_MAX_PRESS_MS) return;
        }

        openModal(modal);
        rerender();
      });
    }

    card.querySelectorAll('[data-open-collection-meal-modal]').forEach((button) => {
      button.addEventListener('click', () => {
        openModal(modal);
        rerender();
      });
    });

    modal.querySelectorAll('[data-close-collection-meal-modal]').forEach((button) => {
      button.addEventListener('click', () => requestModalClose(modal));
    });

    modal.querySelectorAll('[data-option-button]').forEach((button) => {
      button.addEventListener('click', () => {
        const group = button.dataset.optionGroupName;
        const value = normalize(button.dataset.optionValue);
        if (group === 'size') state.size = value;
        if (group === 'protein') {
          if (button.dataset.optionToggle === 'true' && normalize(state.protein) === value) {
            state.protein = normalize(config.defaultProtein || '');
          } else {
            state.protein = value;
          }
        }
        rerender();
      });
    });

    getSauceInputs(modal).forEach((input) => {
      input.addEventListener('change', () => {
        const sauceId = input.dataset.sauceId;
        if (!sauceId) return;

        if (input.checked) {
          state.sauces.add(sauceId);
        } else {
          state.sauces.delete(sauceId);
        }

        rerender();
      });
    });

    const qtyInput = modal.querySelector('[data-modal-quantity-input]');
    const qtyDecrease = modal.querySelector('[data-modal-qty-decrease]');
    const qtyIncrease = modal.querySelector('[data-modal-qty-increase]');

    if (qtyInput) {
      qtyInput.addEventListener('change', () => {
        state.quantity = Math.max(1, Number(qtyInput.value) || 1);
        rerender();
      });
    }

    if (qtyDecrease) {
      qtyDecrease.addEventListener('click', () => {
        state.quantity = Math.max(1, state.quantity - 1);
        rerender();
      });
    }

    if (qtyIncrease) {
      qtyIncrease.addEventListener('click', () => {
        state.quantity += 1;
        rerender();
      });
    }

    const submit = modal.querySelector('[data-modal-submit]');
    const availability = modal.querySelector('[data-modal-availability]');

    if (submit) {
      submit.addEventListener('click', async () => {
        if (submitting) return;

        submitting = true;
        rerender();

        const result =
          mode === 'edit'
            ? await replaceBundleInCart(config, state, modal)
            : await addToCart(config, state, modal);

        if (!result.ok) {
          submitting = false;
          rerender();
          if (availability) availability.textContent = result.error;
          return;
        }

        requestModalClose(modal, {
          afterClose: () => {
            submitting = false;
            rerender();
            if (mode === 'edit' && config.reloadOnSuccess !== false) {
              window.location.reload();
            }
          },
        });
        showFeedback(card, String(config.successMessage || (mode === 'edit' ? 'Updated cart' : 'Added to cart')));
      });
    }

    rerender();
  }

  function initCards() {
    document.querySelectorAll('[data-collection-meal-card]').forEach((card, index) => {
      initCard(card, index);
    });
  }

  function scheduleInitCards() {
    if (initCardsFrame) return;
    initCardsFrame = window.requestAnimationFrame(() => {
      initCardsFrame = 0;
      initCards();
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeModal) {
      requestModalClose(activeModal);
    }
  });

  window.addEventListener('popstate', (event) => {
    historyNavigationPending = false;

    const targetModalState = getModalHistoryState(event.state);
    const targetModalKey = targetModalState ? targetModalState.modalKey : '';
    const currentModalKey = getModalKey(activeModal);

    if (activeModal && currentModalKey !== targetModalKey) {
      closeModal(activeModal);
    }

    initCards();
    if (!targetModalKey) return;

    restoreModalFromHistory(targetModalKey);
    window.requestAnimationFrame(() => {
      initCards();
      restoreModalFromHistory(targetModalKey);
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    initCards();

    const observer = new MutationObserver(() => {
      scheduleInitCards();
    });

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  });
})();
